/**
 * SearchResultsPage
 * 
 * Page displaying search results with filters.
 */

import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSearchParams } from 'react-router-dom';
import { useSearch } from '../lib/hooks/use-collections';
import { ProductGrid } from '../components/organisms/ProductGrid';
import { ProductFilters } from '../components/organisms/ProductFilters';
import { SearchBar } from '../components/organisms/SearchBar';
import { SearchFilters } from '../domain/entities/collection.entity';
import { FunnelExperience, FunnelErrorBoundary } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '../lib/hooks/use-funnel-add-to-cart';
import { useIntl } from '../lib/intl';

const PageContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Header = styled.header`
  margin-bottom: 32px;
`;

const Breadcrumb = styled.nav`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;

  a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 0;
`;

const MainContent = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: column;
  }
`;

const Sidebar = styled.aside`
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 100%;
  }
`;

const ProductsSection = styled.main`
  flex: 1;
  min-width: 0;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const ResultsInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${({ isActive }) => (isActive ? '#3b82f6' : '#e5e7eb')};
  background: ${({ isActive }) => (isActive ? '#3b82f6' : 'white')};
  color: ${({ isActive }) => (isActive ? 'white' : '#374151')};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: ${({ isActive }) => (isActive ? 'white' : '#3b82f6')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  background: #fef2f2;
  border-radius: 12px;
  color: #991b1b;
`;

export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParameters = Object.fromEntries(searchParams.entries());

  // Parse initial filters from URL
  const getFiltersFromParams = (): SearchFilters => ({
    q: searchParams.get('q') || '',
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: 20,
    sort: searchParams.get('sort') || undefined,
    tags: searchParams.getAll('tags'),
    priceMin: searchParams.get('price_min') ? parseFloat(searchParams.get('price_min')!) : undefined,
    priceMax: searchParams.get('price_max') ? parseFloat(searchParams.get('price_max')!) : undefined,
  });

  const [filters, setFilters] = useState<SearchFilters>(getFiltersFromParams);
  const { data, loading, error, search } = useSearch();

  // Perform search when filters change
  useEffect(() => {
    search(filters);
  }, [filters, search]);

  const handleFilterChange = useCallback((newFilters: SearchFilters) => {
    setFilters(newFilters);

    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.q) params.set('q', newFilters.q);
    if (newFilters.page && newFilters.page > 1) params.set('page', newFilters.page.toString());
    if (newFilters.sort) params.set('sort', newFilters.sort);
    if (newFilters.tags) newFilters.tags.forEach((t) => params.append('tags', t));
    if (newFilters.priceMin) params.set('price_min', newFilters.priceMin.toString());
    if (newFilters.priceMax) params.set('price_max', newFilters.priceMax.toString());

    setSearchParams(params);
  }, [setSearchParams]);

  const handleSearch = useCallback((query: string) => {
    handleFilterChange({ ...filters, q: query, page: 1 });
  }, [filters, handleFilterChange]);

  const handlePageChange = useCallback((page: number) => {
    handleFilterChange({ ...filters, page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filters, handleFilterChange]);

  const products = data?.products || [];
  const facets = data?.facets || {};
  const pagination = data?.pagination;

  const searchQuery = filters.q;

  const handleFunnelAddToCart = useFunnelAddToCart();
  const { countryConfig } = useIntl();

  return (
    <PageContainer>
      <Header>
        <Breadcrumb>
          <a href="/">Home</a> / Search
        </Breadcrumb>
        <Title>
          {searchQuery ? `Results for "${searchQuery}"` : 'All Products'}
        </Title>
        {pagination && (
          <Subtitle>
            {pagination.total} {pagination.total === 1 ? 'product' : 'products'} found
          </Subtitle>
        )}
      </Header>

      <MainContent>
        <Sidebar>
          <ProductFilters
            facets={facets}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </Sidebar>

        <ProductsSection>
          <SearchContainer>
            <SearchBar
              placeholder="Search products..."
              initialValue={filters.q || ''}
              onSearch={handleSearch}
              navigateOnSearch={false}
            />
          </SearchContainer>

          {error && (
            <ErrorState>
              <p>Failed to search products: {error}</p>
            </ErrorState>
          )}

          {!error && pagination && (
            <ResultsInfo>
              Showing {products.length} of {pagination.total} products
            </ResultsInfo>
          )}

          <ProductGrid products={products} loading={loading} />

          {pagination && pagination.totalPages > 1 && (
            <Pagination>
              <PageButton
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </PageButton>

              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <PageButton
                    key={pageNum}
                    isActive={pageNum === pagination.page}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </PageButton>
                );
              })}

              {pagination.totalPages > 5 && (
                <>
                  <span style={{ padding: '8px' }}>...</span>
                  <PageButton onClick={() => handlePageChange(pagination.totalPages)}>
                    {pagination.totalPages}
                  </PageButton>
                </>
              )}

              <PageButton
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </ProductsSection>
      </MainContent>

      <FunnelErrorBoundary resetKey={window.location.pathname}>
        <FunnelExperience
          pageType="collection"
          context={{
            url: '/search',
            country: countryConfig.countryCode,
            urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
          }}
          onAddToCart={handleFunnelAddToCart}
        />
      </FunnelErrorBoundary>
    </PageContainer>
  );
}

export default SearchResultsPage;
